import { inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import {
  EntityReference,
  IAmount,
  IArticleKeyPrice,
  ICurrency,
  ICurrencyEntityReference,
  IInitPriceFormat,
  IUsePrice,
  PositionOfCurrencySign
} from '../../models';
import { InformationService } from '../../services';

type FormatCurrencyValue = number | string | IArticleKeyPrice | IAmount | IUsePrice | undefined | null;

type FormatCurrencyCurrency = ICurrency | EntityReference | ICurrencyEntityReference | undefined;

interface IFormatCurrencyCustomOptions {
  price: FormatCurrencyValue;
  currency: FormatCurrencyCurrency;
}

function isIFormatCurrencyCustomOptions(value: any): value is IFormatCurrencyCustomOptions {
  return value && typeof value === 'object' && 'price' in value && 'currency' in value;
}

/**
 * Formats a given value into a currency string based on the provided parameters.
 * @param value The value to be formatted, which can be a number, string, or an IArticleKeyPrice object.
 * @param position Position of the currency sign, either 'before' or 'after' the amount.
 * @param decimalSeparator Separator for decimal values, e.g. 10,99 €
 * @param thousandSeparator Separator for thousand values, e.g. 1.000,99 €
 * @returns Formatted currency string.
 */
@Pipe({
  name: 'formatCurrency',
  standalone: true
})
export class FormatCurrencyPipe implements PipeTransform {
  // Services
  protected readonly informationService: InformationService = inject(InformationService);
  protected readonly locale: string = inject(LOCALE_ID);

  // Methods
  /**
   * Formats a given value into a currency string based on the provided parameters.
   * @param value The value to be formatted, which can be a number, string, or an IArticleKeyPrice object.
   * @param position Position of the currency sign, either 'before' or 'after' the amount.
   * @param decimalSeparator Separator for decimal values, e.g. 10,99 €
   * @param thousandSeparator Separator for thousand values, e.g. 1.000,99 €
   * @returns Formatted currency string.
   */
  transform(
    value: FormatCurrencyValue | IFormatCurrencyCustomOptions,
    position?: PositionOfCurrencySign,
    decimalSeparator?: string,
    thousandSeparator?: string,
    spacingSeparator?: string
  ): string {
    // Parse the input value to extract price and currency
    const parsed = this.parsePriceAndCurrency(value);
    if (!parsed) return '';
    const { price, currency } = parsed;
    if (price == null || isNaN(price)) return '';
    // Get formatting options from init and format the price
    const formatOptions = this.informationService.init()?.priceFormatting;
    return this.getFormattedPrice(price, currency, {
      decimalSeparator: decimalSeparator ?? formatOptions?.decimalSeparator ?? ',',
      thousandSeparator: thousandSeparator ?? formatOptions?.thousandSeparator ?? '.',
      positionOfSign: position ?? formatOptions?.positionOfSign ?? PositionOfCurrencySign.After,
      spacingSeparator: spacingSeparator ?? formatOptions?.spacingSeparator ?? ' '
    });
  }

  /**
   * Parses the input value to extract the price and currency information.
   * @param value The input value which can be a number, string, or an IArticleKeyPrice object.
   * @returns An object containing the extracted price and currency, or undefined if parsing fails.
   */
  private parsePriceAndCurrency(
    value: FormatCurrencyValue | IFormatCurrencyCustomOptions
  ): { price?: number; currency?: FormatCurrencyCurrency } | undefined {
    let priceValue: FormatCurrencyValue = isIFormatCurrencyCustomOptions(value) ? value.price : value;
    if (priceValue == null || priceValue === '') return undefined;
    let price: number | undefined;
    let currency: FormatCurrencyCurrency = isIFormatCurrencyCustomOptions(value) ? value.currency : undefined;

    if (typeof priceValue === 'number' || typeof priceValue === 'string') {
      price = typeof priceValue === 'number' ? priceValue : parseFloat(priceValue.replace(',', '.'));
    } else if (typeof priceValue == 'object') {
      if ('value' in priceValue && priceValue.value !== undefined) {
        price = priceValue.value;
        if (priceValue.currency && !currency) currency = priceValue.currency;
      } else if ('price' in priceValue && priceValue.price !== undefined) {
        price = priceValue.price;
        if (priceValue.currency && !currency) currency = priceValue.currency;
      } else if ('minKeyPrice' in priceValue && priceValue.minKeyPrice?.value != null) {
        price = priceValue.minKeyPrice.value;
        if (priceValue.minKeyPrice.currency && !currency) currency = priceValue.minKeyPrice.currency;
      }
    } else return undefined;
    return { price, currency };
  }

  /**
   * Formats the price according to the specified options.
   * @param price The numeric price to be formatted.
   * @param currency The currency object used for formatting.
   * @param options Formatting options including decimal and thousand separators, position of the currency sign, and spacing.
   * @returns Formatted price string.
   */
  private getFormattedPrice(price: number, currency: FormatCurrencyCurrency, options: Required<IInitPriceFormat>): string {
    const priceString = price.toFixed(2).replace('.', options.decimalSeparator);
    const parts = priceString.split(options.decimalSeparator);
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, options.thousandSeparator);
    const formattedPrice = parts.join(options.decimalSeparator);
    let sign: string | undefined;
    // Has the given currency a sign?
    if (currency && 'sign' in currency && currency.sign) sign = currency.sign;
    else {
      // Resolve currency from information service (by ID if available, otherwise default)
      const resolvedCurrency = this.resolveCurrency(currency);
      sign = resolvedCurrency?.sign;
    }
    if (!sign) return formattedPrice;
    if (options.positionOfSign === PositionOfCurrencySign.Before) {
      return `${sign}${options.spacingSeparator}${formattedPrice}`;
    }

    // Default to placing the sign after the amount for any other (including legacy) values.
    return `${formattedPrice}${options.spacingSeparator}${sign}`;
  }

  /**
   * Resolves the full currency object from the information service.
   * Fallback chain: currency ID (if provided and > 0) > defaultCurrencyID from init > first available currency.
   * When no currency reference is provided, skips the ID lookup and falls back directly to default.
   * @param currency Optional currency reference to resolve by ID.
   * @returns The resolved ICurrency or undefined if no currencies are available.
   */
  private resolveCurrency(currency?: EntityReference | ICurrencyEntityReference): ICurrency | undefined {
    const currencies = this.informationService.currencies();
    if (!currencies?.length) return undefined;
    if (currency && currency.iD > 0) {
      const found = currencies.find((c) => c.iD === currency.iD);
      if (found) return found;
    }
    const defaultCurrencyID = this.informationService.init()?.defaultCurrencyID;
    if (defaultCurrencyID) {
      const found = currencies.find((c) => c.iD === defaultCurrencyID);
      if (found) return found;
    }
    return currencies[0];
  }
}
